import { type Brand, illustrationFor } from "@shared/brand.types";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BrandRoot } from "@/components/BrandRoot";
import { LinkButton } from "@/components/ds/LinkButton";
import { PageTitle, SchoolMasthead } from "@/components/FormMasthead";
import { OfferRequestForm } from "@/components/form/OfferRequestForm";
import { type ExtractionResult, PasteStep } from "@/components/form/PasteStep";
import { PageMessage } from "@/components/PageMessage";
import { SplitLayout } from "@/components/SplitLayout";
import { type BrandView, fetchBrand } from "@/lib/brandApi";
import { copy } from "@/lib/copy";
import { type KnownContact, useKnownContact } from "@/lib/useKnownContact";

/** La page publique de l'école : c'est l'URL qu'elle met sur son propre site. */
export function DepositPage() {
	const { slug = "" } = useParams();
	const [brand, setBrand] = useState<BrandView | null>(null);
	const [loaded, setLoaded] = useState(false);
	// ⚠️ Distinct de `!brand`. Sans `.catch()`, une coupure ou un délai dépassé laissait
	// `loaded` à faux POUR TOUJOURS : « Chargement… » au milieu d'un écran vide, sur la
	// page publique que l'école met sur son site, celle que voient toutes les
	// entreprises. Et le confondre avec « école inconnue » enverrait vérifier une
	// adresse qui est bonne.
	const [echec, setEchec] = useState(false);
	const [sent, setSent] = useState(false);
	const { loaded: contactLoaded, contact: known } = useKnownContact();

	useEffect(() => {
		fetchBrand(slug)
			.then((result) => {
				setBrand(result);
				setLoaded(true);
			})
			.catch(() => {
				setEchec(true);
				setLoaded(true);
			});
	}, [slug]);

	useEffect(() => {
		if (brand) {
			document.title = `${brand.pageTitle ?? copy.deposit.defaultTitle} · ${brand.schoolName}`;
		}
	}, [brand]);

	if (!loaded) {
		return (
			<main className="t-body grid min-h-screen place-items-center text-mg-secondary">
				{copy.deposit.loading}
			</main>
		);
	}

	// Les libellés rendus ici, tels qu'ils sont écrits dans `src/lib/copy.ts` :
	//   loadFailedTitle : « Page indisponible »
	//   loadFailedBody  : « Nous n'avons pas pu charger cette page. Rechargez-la dans un instant. »
	//   retry           : « Recharger la page »
	if (echec) {
		return (
			<PageMessage
				title={copy.deposit.loadFailedTitle}
				body={copy.deposit.loadFailedBody}
				retry={{ label: copy.deposit.retry, onClick: () => window.location.reload() }}
			/>
		);
	}

	if (!brand) {
		return <PageMessage title={copy.deposit.unknownTitle} body={copy.deposit.unknownBody} />;
	}

	return (
		<BrandRoot brand={brand}>
			<SplitLayout
				illustration={illustrationFor(brand)}
				masthead={<SchoolMasthead brand={brand} />}
			>
				{sent ? (
					<Sent brand={brand} known={known !== null} />
				) : (
					<Composer
						brand={brand}
						known={known}
						contactLoaded={contactLoaded}
						onSubmitted={() => setSent(true)}
					/>
				)}
			</SplitLayout>
		</BrandRoot>
	);
}

/**
 * Deux chemins vers le même dépôt, et le premier coûte le moins à l'entreprise.
 * Sans fournisseur de lecture configuré, l'étape n'existe pas : on ne propose
 * jamais « collez votre annonce » pour répondre ensuite qu'on ne sait pas le faire.
 */
function Composer({
	brand,
	known,
	contactLoaded,
	onSubmitted,
}: {
	brand: BrandView;
	known: KnownContact | null;
	/** Le formulaire sème son état à son montage : une session arrivée après ne le remplirait plus. */
	contactLoaded: boolean;
	onSubmitted: () => void;
}) {
	const [step, setStep] = useState<"paste" | "form">(brand.extractionAvailable ? "paste" : "form");
	const [extraction, setExtraction] = useState<ExtractionResult | null>(null);

	if (step === "paste") {
		return (
			<div className="mg-rise">
				<PageTitle title={copy.paste.title} intro={copy.paste.intro} />
				<div className="mt-9">
					<PasteStep
						brand={brand}
						onExtracted={(result) => {
							setExtraction(result);
							setStep("form");
						}}
						onSkip={() => setStep("form")}
					/>
				</div>
				<Footnotes brand={brand} />
			</div>
		);
	}

	// L'attente ne porte QUE sur le formulaire, jamais sur la page.
	//
	// Le cas normal est l'inconnu : faire patienter tout le monde devant un écran
	// vide pour un préremplissage qui ne concerne qu'une minorité serait payer le
	// confort des uns avec la première impression des autres. Et en pratique
	// l'attente ne se voit pas : l'écran de collage s'affiche d'abord, la réponse est
	// arrivée depuis longtemps quand le formulaire s'ouvre.
	if (!contactLoaded) {
		return <p className="mt-9 t-body text-mg-secondary">{copy.deposit.loading}</p>;
	}

	return (
		<div className="mg-rise">
			<PageTitle title={brand.pageTitle ?? copy.deposit.defaultTitle} intro={brand.pageIntro} />

			{extraction ? (
				<ExtractionNotice result={extraction} onRestart={() => setStep("paste")} />
			) : null}

			{known ? (
				<p className="t-body mt-6 rounded-field bg-mg-hover px-4 py-3 text-mg-heading">
					Vous déposez au nom de{" "}
					<span className="font-semibold">{known.company?.name ?? known.email}</span>. Votre demande
					rejoindra les autres dans votre espace.
				</p>
			) : null}

			<div className="mt-9">
				<OfferRequestForm
					brand={brand}
					onSubmitted={onSubmitted}
					// Le nom d'entreprise est extrait, validé et testé : ne pas le reporter
					// obligeait à retaper une information déjà trouvée, sur un champ obligatoire.
					initialValues={{
						...(extraction?.fields ?? {}),
						...(extraction?.companyName ? { companyName: extraction.companyName } : {}),
						// La session prime sur l'extraction pour le CONTACT : le modèle a lu
						// l'annonce, pas la personne qui dépose.
						...(known
							? {
									contactEmail: known.email,
									...(known.fullName ? { contactName: known.fullName } : {}),
								}
							: {}),
					}}
					initialCompany={known?.company ?? null}
				/>
			</div>

			<Footnotes brand={brand} />
		</div>
	);
}

/** Ce qui a été lu, et surtout ce qui ne l'a pas été : taire les trous les fait passer. */
function ExtractionNotice({
	result,
	onRestart,
}: {
	result: ExtractionResult;
	onRestart: () => void;
}) {
	const filled = Object.keys(result.fields).length;

	return (
		<div className="mt-6 rounded-card border border-mg-border bg-mg-hover p-4">
			<p className="t-body-md text-mg-heading">{copy.paste.reviewTitle}</p>
			<p className="t-body mt-1 text-mg-secondary">
				{copy.paste.reviewFilled(filled)}
				{result.missing.length > 0 ? ` ${copy.paste.reviewMissing(result.missing.length)}` : ""}
			</p>
			<button
				type="button"
				onClick={onRestart}
				className="t-body mt-2 text-mg-action underline underline-offset-2"
			>
				{copy.paste.reviewBack}
			</button>
		</div>
	);
}

/**
 * Ce qui était des cartes à ombre dans une colonne de droite redevient ce que c'est :
 * des notes de bas de page. Workable fait pareil, en petit gris et sans cadre. Une
 * carte encadrée à côté du formulaire lui fait concurrence au lieu de l'accompagner.
 */
function Footnotes({ brand }: { brand: Brand }) {
	const contact = [brand.contactName, brand.contactEmail].filter(Boolean).join(" · ");

	return (
		<div className="mt-10 flex flex-col gap-2 border-t border-mg-border pt-6 t-body text-mg-secondary">
			<p>{copy.deposit.noAccount}</p>
			{contact ? (
				<p>
					{copy.deposit.contactTitle} : <span className="text-mg-heading">{brand.contactName}</span>
					{brand.contactEmail ? (
						<>
							{" · "}
							<a
								href={`mailto:${brand.contactEmail}`}
								className="text-mg-action underline underline-offset-2"
							>
								{brand.contactEmail}
							</a>
						</>
					) : null}
				</p>
			) : null}
			<p>
				{copy.deposit.alreadyDeposited}{" "}
				<Link to="/mes-demandes" className="text-mg-action underline underline-offset-2">
					{copy.deposit.alreadyDepositedLink}
				</Link>
			</p>
		</div>
	);
}

/** La confirmation est un moment, pas un paragraphe : c'est la seule récompense du parcours. */
function Sent({ brand, known }: { brand: Brand; known: boolean }) {
	return (
		<section className="mg-rise">
			<span className="grid h-16 w-16 place-items-center rounded-full bg-mg-hover">
				<svg viewBox="0 0 24 24" className="h-8 w-8 text-mg-action" fill="none" aria-hidden>
					<title>Envoyé</title>
					<path
						d="M4 12.5 9.5 18 20 6.5"
						stroke="currentColor"
						strokeWidth="2.2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</span>
			<h1 className="mt-7 t-h1 text-mg-heading">{copy.deposit.sentTitle}</h1>
			<p className="t-body mt-3 max-w-md text-mg-secondary">
				{brand.confirmationMessage ?? copy.deposit.sentFallback(brand.schoolName)}
			</p>
			{/* Une entreprise déjà connectée retourne DANS son espace : lui repasser par
			    la saisie d'email lui redemanderait un code qu'elle a déjà donné. */}
			<LinkButton
				to={known ? "/espace/offres" : "/mes-demandes"}
				tone="primary"
				size="md"
				className="mt-8"
			>
				{known ? copy.deposit.backToEspace : copy.deposit.trackCta}
			</LinkButton>
		</section>
	);
}
